import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mountain,
  Sun,
  Moon,
  Wind,
  Coffee,
  Waves,
  Eye,
  Camera,
  Calendar,
  Phone,
  ArrowRight,
  CheckCircle,
  MapPin,
  Sparkles,
  ChevronDown,
  Compass,
  Maximize2
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import BookingSearchModal from '../../components/BookingSearchModal';
import { trackBookNow, trackWhatsAppClick, trackPageView } from '../../utils/analytics';
import { buildLandingRoomCards } from '../../utils/landingRooms';
import LandingRoomCardGrid from '../../components/public/LandingRoomCardGrid';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MountainViewHotelBatu = () => {
  const navigate = useNavigate();
  const [showBookingSearch, setShowBookingSearch] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [content, setContent] = useState({});
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    document.title = "Hotel Mountain View di Batu Malang | Spencer Green Hotel Pemandangan Gunung";
    trackPageView('/mountain-view-hotel-batu');

    const fetchPageContent = async () => {
      try {
        const res = await axios.get(`${API_URL}/content/mountain_view`);
        if (Array.isArray(res.data)) {
          const map = {};
          res.data.forEach(item => {
            map[item.section] = item.content;
          });
          setContent(map);
        }
      } catch (err) {
        console.error('Failed to fetch mountain_view content:', err);
      }
    };

    const fetchRooms = async () => {
      try {
        const res = await axios.get(`${API_URL}/rooms`);
        if (res.data && res.data.length > 0) {
          setRooms(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch rooms:', err);
      }
    };

    fetchPageContent();
    fetchRooms();
  }, []);

  const handleBookNow = (context = 'Mountain View Page') => {
    trackBookNow(`Mountain View Landing - ${context}`);
    setShowBookingSearch(true);
  };

  const handleWhatsApp = (topic = 'Request Kamar View Gunung') => {
    trackWhatsAppClick(`Mountain View Landing - ${topic}`);
    const message = `Halo Spencer Green Hotel Batu, saya ingin memesan kamar dengan view pegunungan terbaik (balkon/lantai atas). Mohon info ketersediaan dan promo.`;
    window.open(`https://wa.me/6281130700206?text=${encodeURIComponent(message)}`, '_blank');
  };

  const heroContent = content.hero || {};
  const ctaContent = content.cta || {};
  const roomCards = buildLandingRoomCards(rooms, content.rooms);

  const mountainFeatures = [
    {
      title: content.feature_1?.title || "Panorama Gunung Panderman & Lereng Arjuno",
      desc: content.feature_1?.desc || "Nikmati kemegahan siluet Gunung Panderman dan bukit hijau Punten Bumiaji tanpa terhalang gedung-gedung bertingkat.",
      icon: <Mountain className="w-6 h-6 text-emerald-600" />
    },
    {
      title: content.feature_2?.title || "Balkon Privat Menghadap Bukit",
      desc: content.feature_2?.desc || "Setiap kamar tipe Deluxe & Executive dilengkapi balkon pribadi untuk menikmati kopi pagi berteman sejuknya kabut pegunungan.",
      icon: <Eye className="w-6 h-6 text-emerald-600" />
    },
    {
      title: content.feature_3?.title || "Hawa Dingin Alami 18°C – 22°C",
      desc: content.feature_3?.desc || "Berada di dataran tinggi Punten, rasakan kesejukan murni yang menyegarkan tubuh dan pikiran, bebas polusi udara perkotaan.",
      icon: <Wind className="w-6 h-6 text-emerald-600" />
    },
    {
      title: content.feature_4?.title || "Rooftop Cafe & Kolam Renang Berlatar Alam",
      desc: content.feature_4?.desc || "Rintik Rindu Pool Cafe di rooftop menawarkan pemandangan sunset memukau di kala senja dan gemerlap city light di malam hari.",
      icon: <Coffee className="w-6 h-6 text-emerald-600" />
    }
  ];

  const viewTimes = [
    {
      title: content.time_1?.title || "Pagi Hari: Lautan Kabut & Sunrise Emas",
      desc: content.time_1?.desc || "Bangun tidur dengan embun segar di kaca jendela dan semburat cahaya fajar keemasan yang menyinari puncak perbukitan.",
      icon: <Sun className="w-5 h-5 text-amber-500" />,
      tag: content.time_1?.tag || "05.30 - 08.00 WIB"
    },
    {
      title: content.time_2?.title || "Siang Hari: Kontur Bukit Hijau Mempesona",
      desc: content.time_2?.desc || "Langit biru bersih berpadu dengan hijaunya kebun apel dan pepohonan pinus perbukitan Punten Bumiaji.",
      icon: <Compass className="w-5 h-5 text-emerald-500" />,
      tag: content.time_2?.tag || "10.00 - 15.00 WIB"
    },
    {
      title: content.time_3?.title || "Senja Hari: Sunset Magis di Balik Gunung",
      desc: content.time_3?.desc || "Saksikan langit jingga keunguan dari balkon kamar atau area kolam renang outdoor saat matahari perlahan tenggelam.",
      icon: <Sparkles className="w-5 h-5 text-orange-500" />,
      tag: content.time_3?.tag || "16.30 - 18.00 WIB"
    },
    {
      title: content.time_4?.title || "Malam Hari: Kerlap-Kerlip Lampu Kota (City Light)",
      desc: content.time_4?.desc || "Udara dingin malam yang syahdu ditemani teh hangat sambil memandang ribuan lampu Kota Batu dari ketinggian.",
      icon: <Moon className="w-5 h-5 text-indigo-400" />,
      tag: content.time_4?.tag || "18.30 - 22.00 WIB"
    }
  ];

  const galleryImages = [
    {
      url: content.gallery_1?.image || "https://res.cloudinary.com/dgfjos8xa/image/upload/v1767362992/spencer-green-hotel-batu-malang-main-pool-view-building-night_u2ienp.webp",
      caption: content.gallery_1?.caption || "Pemandangan Kolam Renang & Gedung Hotel di Ketinggian"
    },
    {
      url: content.gallery_2?.image || "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800",
      caption: content.gallery_2?.caption || "Kamar Deluxe dengan Jendela Panorama Pegunungan"
    },
    {
      url: content.gallery_3?.image || "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800",
      caption: content.gallery_3?.caption || "Executive Suite Balkon View Bukit Hijau"
    },
    {
      url: content.gallery_4?.image || "https://res.cloudinary.com/dgfjos8xa/image/upload/v1767362983/spencer-green-hotel-batu-malang-main-pool_udaykk.jpg",
      caption: content.gallery_4?.caption || "Berenang di Kolam Outdoor Berlatar Bukit Asri"
    },
    {
      url: content.gallery_5?.image || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800",
      caption: content.gallery_5?.caption || "Makan Malam & Ngopi Santai di Kenari Restaurant"
    },
    {
      url: content.gallery_6?.image || "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800",
      caption: content.gallery_6?.caption || "Suasana Restoran dengan Pemandangan Alam"
    }
  ];

  const faqs = [
    {
      q: "Tipe kamar apa saja yang memiliki pemandangan gunung di Spencer Green Hotel?",
      a: "Tipe Deluxe Room Mountain View dan Executive Suite dilengkapi dengan balkon pribadi dan jendela kaca lebar yang langsung menghadap ke panorama perbukitan hijau dan Gunung Panderman."
    },
    {
      q: "Apakah semua kamar memiliki balkon?",
      a: "Sebagian besar kamar tipe Deluxe dan seluruh tipe Executive Suite memiliki balkon pribadi. Untuk tipe Superior menghadap ke area taman asri hotel. Anda dapat mengajukan request kamar lantai atas/view pegunungan saat reservasi."
    },
    {
      q: "Berapa suhu udara rata-rata di hotel?",
      a: "Karena berada di kawasan Punten, Bumiaji di ketinggian Kota Batu, suhu udara rata-rata berkisar antara 18°C hingga 22°C pada siang hari, dan bisa mencapai 15°C hingga 17°C pada malam hari."
    },
    {
      q: "Di mana spot terbaik untuk melihat sunset atau city light?",
      a: "Spot terbaik adalah di Rintik Rindu Rooftop Pool Cafe di lantai atas dan area tepi kolam renang outdoor, di mana Anda bisa melihat pemandangan 360 derajat pegunungan dan kerlap-kerlip lampu kota Batu."
    },
    {
      q: "Bagaimana cara memastikan saya mendapatkan kamar dengan mountain view?",
      a: "Pilihlah tipe Deluxe Mountain View atau Executive Suite saat reservasi online, atau Anda bisa langsung menghubungi tim reservasi via WhatsApp untuk konfirmasi penempatan kamar di lantai atas dengan view terbaik."
    }
  ];

  return (
    <div className="bg-emerald-50/20 text-gray-800">
      {/* Schema.org */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Hotel",
          "name": "Spencer Green Hotel Batu - Mountain View Hotel",
          "description": "Hotel dengan pemandangan gunung spektakuler di Batu Malang. Balkon privat menghadap Gunung Panderman, hawa sejuk 18°C, kolam renang & rooftop cafe.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Jl. Raya Punten No.86, Bumiaji",
            "addressLocality": "Kota Batu",
            "addressRegion": "Jawa Timur",
            "postalCode": "65338",
            "addressCountry": "ID"
          },
          "telephone": "+6281130700206",
          "url": "https://spencergreenhotel.com/mountain-view-hotel-batu"
        })}
      </script>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroContent.image || "https://res.cloudinary.com/dgfjos8xa/image/upload/v1767362992/spencer-green-hotel-batu-malang-main-pool-view-building-night_u2ienp.webp"}
            alt={heroContent.imageAlt || "Hotel Mountain View Batu Malang - Spencer Green Hotel"}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/60 to-black/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-emerald-600/90 text-white px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-4 backdrop-blur-sm">
              <Mountain className="w-4 h-4" />
              {heroContent.badge || "Hotel View Pegunungan Terbaik di Kota Batu Malang"}
            </div>

            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {heroContent.title || "Bangun Pagi Berteman Pesona Megah Pegunungan Batu"}
            </h1>

            <p className="text-emerald-100 text-base sm:text-xl leading-relaxed mb-8 max-w-3xl mx-auto">
              {heroContent.subtitle || "Rasakan magisnya panorama Gunung Panderman dan lereng perbukitan hijau Punten langsung dari balkon kamar Anda. Berhawa sejuk alami 18°C, bebas polusi, dan menenangkan jiwa."}
            </p>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <Button
                onClick={() => handleBookNow('Hero Mountain View')}
                className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base px-8 py-6 rounded-full shadow-xl transition-all"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Cek Kamar Mountain View
              </Button>

              <Button
                onClick={() => handleWhatsApp('Hero Request View')}
                variant="outline"
                className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border-white/30 font-medium text-base px-6 py-6 rounded-full backdrop-blur-sm"
              >
                <Phone className="w-5 h-5 mr-2 text-emerald-400" />
                Request Kamar View Gunung (WA)
              </Button>
            </div>

            {/* Feature Badges */}
            <div className="flex flex-wrap justify-center items-center gap-6 mt-10 text-white/90 text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Balkon Pribadi View Gunung</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Suhu Dingin Alami 18°C</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Sunset & City Light Rooftop</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Kolam Renang Berlatar Alam</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Keistimewaan Mountain View */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Pemandangan Spektakuler
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Ketenangan Hakiki di Dataran Tinggi Punten
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              Berbeda dari hotel di pusat kota yang padat, Spencer Green Hotel berada di lereng bukit Punten Bumiaji yang menghadirkan suasana tenang dan pemandangan alam bebas tanpa terhalang gedung beton.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mountainFeatures.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-6 rounded-2xl bg-emerald-50/40 border border-emerald-100/70 hover:bg-emerald-50 hover:border-emerald-200 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                    {item.icon}
                  </div>
                  <h3 className="font-display text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 Waktu Terbaik Menikmati Mountain View */}
      <section className="py-16 sm:py-20 bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-300 font-semibold tracking-wider text-xs uppercase bg-emerald-800 px-3 py-1 rounded-full">
              Magisnya 24 Jam
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
              Sensasi Pemandangan Gunung dari Fajar hingga Malam
            </h2>
            <p className="text-emerald-200 text-base sm:text-lg">
              Setiap jam menyuguhkan pesona keindahan alam yang berbeda untuk menemani waktu santai Anda di Spencer Green Hotel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {viewTimes.map((vt, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-emerald-800/60 rounded-2xl p-6 border border-emerald-700/60 hover:border-emerald-500 transition-colors flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 rounded-xl bg-emerald-700/80 flex items-center justify-center">
                      {vt.icon}
                    </div>
                    <span className="text-xs font-mono bg-emerald-950/80 text-emerald-300 px-2.5 py-1 rounded-full border border-emerald-700">
                      {vt.tag}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white mb-2">{vt.title}</h3>
                  <p className="text-emerald-200 text-xs sm:text-sm leading-relaxed">{vt.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Kamar Rekomendasi Mountain View (fallback bila section CMS kosong) */}
      {roomCards.length === 0 && (
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Rekomendasi Kamar
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Pilihan Kamar dengan Pemandangan Gunung Terbaik
            </h2>
            <p className="text-gray-600 text-base sm:text-lg">
              Pilih tipe kamar ber-balkon pribadi untuk pengalaman staycation yang paling menenangkan.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Deluxe Mountain View */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-soft border border-emerald-200 flex flex-col justify-between group hover:shadow-xl transition-shadow">
              <div>
                <div className="relative h-64 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800"
                    alt="Deluxe Room Mountain View"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-emerald-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                    Best Seller View Gunung
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="font-display text-2xl font-bold text-gray-900 mb-2">Deluxe Room Mountain View</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    Kamar favorit dengan balkon pribadi menghadap langsung ke arah perbukitan hijau Punten. Rasakan udara dingin segar pagi hari sambil menikmati secangkir kopi hangat di teras balkon.
                  </p>
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center text-xs text-gray-700 gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>Balkon Pribadi dengan Kursi Santai</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-700 gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>Bathtub & Shower Air Panas 24 Jam</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-700 gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>King Bed / Twin Bed Nyaman</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-700 gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>Termasuk Sarapan Prasmanan Kenari Restaurant</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 pt-0 border-t border-gray-100">
                <span className="text-xs text-gray-400">Mulai dari</span>
                <p className="text-2xl font-bold text-emerald-700 mb-4">
                  Rp 1.200.000 <span className="text-xs text-gray-400 font-normal">/malam</span>
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleBookNow('Deluxe Mountain View')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                  >
                    Pesan Kamar
                  </Button>
                  <Button
                    onClick={() => handleWhatsApp('Request Deluxe Mountain View')}
                    variant="outline"
                    className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl"
                  >
                    Tanya Admin
                  </Button>
                </div>
              </div>
            </div>

            {/* Executive Suite Room */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-soft border border-emerald-200 flex flex-col justify-between group hover:shadow-xl transition-shadow">
              <div>
                <div className="relative h-64 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800"
                    alt="Executive Suite Mountain View"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-emerald-800 text-white text-xs px-3 py-1 rounded-full font-semibold">
                    Kamar Paling Mewah & Luas
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="font-display text-2xl font-bold text-gray-900 mb-2">Executive Suite Room</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    Kamar termewah dengan pemandangan 180 derajat lereng pegunungan. Dilengkapi area living room luas, sofa santai, meja kerja, dan kamar mandi marmer dengan bathtub air panas.
                  </p>
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center text-xs text-gray-700 gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>Jendela Kaca Lebar Panorama View</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-700 gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>Ruang Tamu (Living Room) & Smart TV</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-700 gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>Kamar Mandi Mewah Bathtub Air Hangat</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-700 gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>Prioritas Penempatan Kamar Lantai Atas</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 pt-0 border-t border-gray-100">
                <span className="text-xs text-gray-400">Mulai dari</span>
                <p className="text-2xl font-bold text-emerald-700 mb-4">
                  Rp 1.800.000 <span className="text-xs text-gray-400 font-normal">/malam</span>
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleBookNow('Executive Mountain View')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                  >
                    Pesan Kamar
                  </Button>
                  <Button
                    onClick={() => handleWhatsApp('Request Executive Mountain View')}
                    variant="outline"
                    className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl"
                  >
                    Tanya Admin
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Visual Photo Gallery */}
      <section className="py-16 sm:py-20 bg-emerald-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Galeri Visual
            </span>
            <h2 className="font-display text-3xl font-bold text-gray-900 mt-3 mb-2">
              Suasana Alam di Spencer Green Hotel
            </h2>
            <p className="text-gray-500 text-sm">
              Potret keindahan sudut-sudut hotel berteman pemandangan alam perbukitan Kota Batu.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="rounded-2xl overflow-hidden shadow-soft group relative h-64">
                <img
                  src={img.url}
                  alt={img.caption}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <p className="text-white text-xs sm:text-sm font-medium">{img.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {roomCards.length > 0 && (
        <LandingRoomCardGrid
          title={content.rooms?.title || 'Pilihan Kamar'}
          subtitle={content.rooms?.subtitle || 'Kamar dengan Panorama Pegunungan'}
          cards={roomCards}
          onBook={handleBookNow}
        />
      )}

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              FAQ Wisatawan
            </span>
            <h2 className="font-display text-3xl font-bold text-gray-900 mt-3 mb-2">
              Pertanyaan Seputar Kamar Mountain View
            </h2>
            <p className="text-gray-500 text-sm">
              Jawaban seputar fasilitas kamar ber-view pegunungan dan tips menginap.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = activeFaq === index;
              return (
                <div
                  key={index}
                  className="border border-gray-200 rounded-2xl overflow-hidden transition-colors"
                >
                  <button
                    onClick={() => setActiveFaq(isOpen ? null : index)}
                    className="w-full text-left p-5 flex items-center justify-between font-semibold text-gray-900 hover:text-emerald-700 bg-white transition-colors"
                  >
                    <span className="text-base sm:text-lg pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180 text-emerald-600' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="p-5 pt-0 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 bg-emerald-50/20">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-emerald-800 to-emerald-950 text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h3 className="font-display text-2xl sm:text-4xl font-bold mb-4">
            {ctaContent.title || "Siap Menikmati Sejuknya Pagi dengan View Gunung di Batu?"}
          </h3>
          <p className="text-emerald-200 text-sm sm:text-base mb-8">
            {ctaContent.subtitle || "Dapatkan kamar favorit lantai atas sebelum kehabisan. Reservasi online langsung dengan konfirmasi cepat."}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              onClick={() => handleBookNow('Final CTA Mountain View')}
              className="bg-white text-emerald-950 hover:bg-emerald-50 font-bold px-8 py-6 rounded-full shadow-xl"
            >
              Cek Ketersediaan Kamar
            </Button>
            <Button
              onClick={() => handleWhatsApp('Final WhatsApp Mountain View')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-6 rounded-full"
            >
              Konsultasi Kamar View Gunung (WA)
            </Button>
          </div>
        </div>
      </section>

      {/* Booking Search Modal */}
      <BookingSearchModal
        isOpen={showBookingSearch}
        onClose={() => setShowBookingSearch(false)}
        onSearch={(data) => {
          navigate('/', {
            state: {
              doSearch: true,
              searchParams: data
            }
          });
        }}
      />
    </div>
  );
};

export default MountainViewHotelBatu;
